import { useState } from 'react';
import { Clock, Plus, StickyNote, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import type { DailyReportNote } from '@/types/dailyReport';
import { dailyReportsApi } from '../../api/dailyReports.api';
import { formatInstituteDateTime } from '../../utils';

export interface DailyReportNotesPanelProps {
  notes: DailyReportNote[];
  /** `YYYY-MM-DD` — the day the note belongs to. */
  noteDate: string;
  /** Fires once the server has it, so the page can refresh what it holds. */
  onChanged: () => void;
  /** A closed institute has no day to write about. */
  isDisabled?: boolean;
}

/**
 * What the teacher wants to say about the day itself.
 *
 * Separate from the reports above it, and deliberately: those answer "what
 * happened in 8-A maths", this answers "how was today". A note has no class and
 * no subject, which is why it has a table of its own rather than a nullable
 * pair of columns on the report.
 *
 * **Added, never edited.** Something written after second period and something
 * written after the last one are two notes with two times, not one that
 * overwrote the other — so the button says Add and each note keeps its own
 * timestamp. Withdrawing one is a soft delete on the server; nothing is erased.
 *
 * These are the caller's own notes. The server resolves whose they are from the
 * token, so there is no id on this screen that could reach anybody else's.
 */
export function DailyReportNotesPanel({
  notes,
  noteDate,
  onChanged,
  isDisabled = false,
}: DailyReportNotesPanelProps) {
  const [isWriting, setIsWriting] = useState(false);
  const [draft, setDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);

  function closeWriter() {
    setIsWriting(false);
    setDraft('');
  }

  async function addNote() {
    const content = draft.trim();
    if (!content) return;

    setIsSaving(true);
    try {
      await dailyReportsApi.addNote({ noteDate, content });
      toast.success('Note added', 'It is on your day, with the time you wrote it.');
      closeWriter();
      onChanged();
    } catch (caught) {
      toast.error(
        'Could not add that note',
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function removeNote(uuid: string) {
    setRemoving(uuid);
    try {
      await dailyReportsApi.removeNote(uuid);
      toast.success('Note withdrawn', 'It no longer appears on your day.');
      onChanged();
    } catch (caught) {
      toast.error(
        'Could not withdraw that note',
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
    } finally {
      setRemoving(null);
    }
  }

  return (
    <section className="card flex flex-col rounded-[16px] border border-line bg-surface p-5 shadow-2xs">
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] border border-amber-500/25 bg-amber-500/15 text-amber-600 dark:text-amber-400"
            aria-hidden
          >
            <StickyNote className="h-4 w-4" />
          </span>
          <h3 className="text-base font-bold tracking-normal text-ink">Teacher Notes</h3>
        </div>

        <Button
          variant={isWriting ? 'secondary' : 'primary'}
          size="sm"
          aria-expanded={isWriting}
          disabled={isDisabled}
          leftIcon={isWriting ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          onClick={() => (isWriting ? closeWriter() : setIsWriting(true))}
        >
          {isWriting ? 'Cancel' : 'Add Note'}
        </Button>
      </div>

      {isWriting && (
        <div className="mt-4 flex flex-col gap-2.5">
          <Textarea
            label="Your note about today"
            hideLabel
            rows={3}
            maxLength={2000}
            autoFocus
            value={draft}
            disabled={isSaving}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="How the day went overall, and anything to pick up tomorrow…"
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              isLoading={isSaving}
              disabled={draft.trim().length === 0}
              onClick={() => void addNote()}
            >
              {isSaving ? 'Adding…' : 'Add note'}
            </Button>
          </div>
        </div>
      )}

      {notes.length === 0 && !isWriting ? (
        <p className="mt-5 text-sm font-medium text-ink-3">
          Nothing written about today yet. A note here is about the day as a whole — what each
          class covered belongs in its own report above.
        </p>
      ) : (
        <ul className="mt-4 flex flex-col gap-3">
          {notes.map((note) => (
            <li
              key={note.uuid}
              className="rounded-field border border-line bg-surface-2 p-3.5 text-sm"
            >
              <p className="whitespace-pre-wrap font-medium leading-relaxed text-ink">
                {note.content}
              </p>

              <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 border-t border-line pt-2.5">
                <span className="flex items-center gap-1.5 text-2xs font-semibold text-ink-3">
                  <Clock className="h-3 w-3 shrink-0" aria-hidden />
                  {formatInstituteDateTime(note.savedAt)}
                </span>

                <button
                  type="button"
                  disabled={removing === note.uuid}
                  onClick={() => void removeNote(note.uuid)}
                  aria-label="Withdraw this note"
                  className="inline-flex items-center gap-1 rounded-field px-1.5 py-0.5 text-2xs font-bold text-ink-3 transition-colors hover:text-crit focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line disabled:opacity-50"
                >
                  <Trash2 className="h-3 w-3" aria-hidden />
                  {removing === note.uuid ? 'Withdrawing…' : 'Withdraw'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
